import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ScopeUnit } from "../../util/slopifyScopeMap";
import { useScopeContext } from "./ScopeContext";

export const ScopePill = () => {
  const { t } = useTranslation();
  const { modelName, availableUnits, selectedUnitIds, replaceScope, clearScope } =
    useScopeContext();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (isOpen && containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [isOpen]);

  const isAllSelected = selectedUnitIds.length === 0;

  const handleToggle = useCallback(
    (unitId: string) => {
      const currentlyIncluded = isAllSelected
        ? availableUnits.map((u) => u.id)
        : [...selectedUnitIds];

      if (currentlyIncluded.includes(unitId)) {
        // Unchecking — don't allow removing the last one
        if (currentlyIncluded.length <= 1) return;
        replaceScope(currentlyIncluded.filter((id) => id !== unitId));
      } else {
        // Checking — if all are now included, clear to empty (= whole content)
        const next = [...currentlyIncluded, unitId];
        if (next.length === availableUnits.length) {
          clearScope();
        } else {
          replaceScope(next);
        }
      }
    },
    [isAllSelected, availableUnits, selectedUnitIds, replaceScope, clearScope],
  );

  // Hide for content types with 0 or 1 scope units (nothing to choose).
  if (availableUnits.length <= 1) return null;

  const isUnitIncluded = (unitId: string) => isAllSelected || selectedUnitIds.includes(unitId);
  const includedCount = isAllSelected ? availableUnits.length : selectedUnitIds.length;

  const unitLabel = (unit: ScopeUnit) =>
    t(
      `general.assistantScenarIa.scope.units.${modelName}.${unit.labelKey}`,
      unit.labelParams,
    ) as string;

  return (
    <div ref={containerRef} className="relative mb-2">
      <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-[11px] text-gray-600">
        <span>
          {t("general.assistantScenarIa.scopePillLabel", {
            count: includedCount,
            total: availableUnits.length,
          })}
        </span>
        <span className="text-gray-300">&middot;</span>
        <button
          type="button"
          className="text-green-500 hover:text-green-600"
          onClick={() => setIsOpen((v) => !v)}
          aria-expanded={isOpen}
        >
          {t("general.assistantScenarIa.contextEdit")}
        </button>
      </div>
      {isOpen && (
        <div className="absolute z-10 mt-1 left-0 w-64 rounded-md border border-gray-200 bg-white shadow-sm p-2">
          <div
            className="text-[11px] text-gray-600"
            role="group"
            aria-label={t("general.assistantScenarIa.scopePillTitle") as string}
          >
            <div className="font-medium text-gray-700 mb-1.5">
              {t("general.assistantScenarIa.scopePillTitle")}
            </div>
            <ul className="space-y-1">
              {availableUnits.map((unit) => {
                const checked = isUnitIncluded(unit.id);
                return (
                  <li key={unit.id} className="flex items-center gap-2">
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5 accent-green-500"
                        checked={checked}
                        onChange={() => handleToggle(unit.id)}
                      />
                    </label>
                    <span
                      className={`flex-1 truncate ${checked ? "" : "line-through text-gray-400"}`}
                    >
                      {unitLabel(unit)}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScopePill;
